// AI Service - Sikker integrasjon via server-side proxy
// API-nøkkelen sendes ALDRI til klienten
class AIService {
  constructor() {
    this.endpoint = '/.netlify/functions/identify-fish';
    this.maxRetries = 3;
    this.timeout = 30000;
  }

  // Validerer og renser input før sending
  validateAndSanitizeInput(base64Image) {
    if (!base64Image || typeof base64Image !== 'string') {
      throw new Error('Ugyldig bildedata');
    }

    const base64Regex = /^data:image\/(jpeg|jpg|png|webp);base64,/;
    if (!base64Regex.test(base64Image)) {
      throw new Error('Ugyldig bildeformat. Kun JPEG, PNG og WebP støttes.');
    }

    if (base64Image.length > 5500000) {
      throw new Error('Bildet er for stort. Maks 4MB.');
    }

    return base64Image;
  }

  // Hovedfunksjon for fiskeidentifikasjon
  async identifyFish(base64Image, onProgress = null) {
    try {
      if (onProgress) onProgress('Forbereder analyse...');

      const sanitizedImage = this.validateAndSanitizeInput(base64Image);
      const result = await this.makeSecureAPICall(sanitizedImage, onProgress);
      return this.validateAIResponse(result);
    } catch (error) {
      console.error('AI identifikasjon feilet:', error);
      throw new Error(`Kunne ikke identifisere fisken: ${error.message}`);
    }
  }

  // Sikker API-kall via Netlify Function (nøkkelen er server-side)
  async makeSecureAPICall(base64Image, onProgress, attempt = 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      if (onProgress) onProgress(`Analyserer bilde (forsøk ${attempt}/${this.maxRetries})...`);

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ image: base64Image })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 429 && attempt < this.maxRetries) {
          await this.delay(Math.pow(2, attempt) * 1000);
          return this.makeSecureAPICall(base64Image, onProgress, attempt + 1);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API-feil: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error('Forespørselen tok for lang tid');
      }

      if (attempt < this.maxRetries && !error.message.includes('API-feil')) {
        await this.delay(Math.pow(2, attempt) * 1000);
        return this.makeSecureAPICall(base64Image, onProgress, attempt + 1);
      }

      throw error;
    }
  }

  // Valider AI-respons og rens data
  validateAIResponse(apiResponse) {
    try {
      const content = apiResponse?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!content) {
        throw new Error('Tom respons fra AI');
      }

      const cleanedContent = content.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleanedContent);

      if (!parsed.species || typeof parsed.species !== 'string') {
        throw new Error('Ugyldig respons-struktur');
      }

      return {
        species: this.sanitizeSpeciesName(parsed.species),
        latin: this.sanitizeLatinName(parsed.latin || ''),
        confidence: this.calculateConfidence(parsed.species)
      };
    } catch (error) {
      console.error('Kunne ikke parse AI-respons:', error);
      return { species: 'Ukjent art', latin: '', confidence: 0 };
    }
  }

  sanitizeSpeciesName(species) {
    if (!species || typeof species !== 'string') return 'Ukjent art';
    const cleaned = species
      .replace(/<[^>]*>/g, '')
      .replace(/[^\w\sæøåÆØÅ()-]/g, '')
      .trim()
      .slice(0, 50);
    return cleaned || 'Ukjent art';
  }

  sanitizeLatinName(latin) {
    if (!latin || typeof latin !== 'string') return '';
    return latin.replace(/<[^>]*>/g, '').replace(/[^\w\s.-]/g, '').trim().slice(0, 100);
  }

  calculateConfidence(species) {
    const knownSpecies = [
      'Torsk', 'Lyr', 'Sei', 'Makrell', 'Laks', 'Sjøørret', 'Kveite', 'Breiflabb',
      'Ørret', 'Røye', 'Abbor', 'Gjedde'
    ];
    return knownSpecies.some(k => species.toLowerCase().includes(k.toLowerCase())) ? 0.9 : 0.6;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default new AIService();
